import { ClubDocument, type Club, type ClubPlayer, documentChasesCurrentMembers } from "@mixitone/models";
import dayjs from "dayjs";

/** True when a contact field has non-whitespace content (null/undefined/"" do not count). */
export function hasContactDetail(value: string | null | undefined): boolean {
  return Boolean(value?.trim());
}

export const NOT_PLAYED_RECENTLY_DAYS = 90;

export type MemberBookAttentionReason =
  | "missing_email"
  | "missing_phone"
  | "inactive"
  | "document_outstanding"
  | "not_played_recently";

export type ClubPlayerLastPlayed = {
  nights_played: number;
  date_of_last_night_played: string;
};

export function getMemberBookAttentionReasons(
  player: ClubPlayer,
  _club: Club,
  lastPlayed: ClubPlayerLastPlayed | undefined,
  requiredDocuments: ClubDocument[],
): MemberBookAttentionReason[] {
  const reasons: MemberBookAttentionReason[] = [];

  if (!player.active) {
    reasons.push("inactive");
  }

  if (!hasContactDetail(player.email)) {
    reasons.push("missing_email");
  }

  if (!hasContactDetail(player.phone)) {
    reasons.push("missing_phone");
  }

  if (playerHasDocumentOutstanding(player, requiredDocuments)) {
    reasons.push("document_outstanding");
  }

  const lastDate = lastPlayed?.date_of_last_night_played;
  if (lastDate) {
    if (dayjs().diff(dayjs(lastDate), "day") > NOT_PLAYED_RECENTLY_DAYS) {
      reasons.push("not_played_recently");
    }
  } else if ((lastPlayed?.nights_played ?? 0) === 0) {
    reasons.push("not_played_recently");
  }

  return reasons;
}

/**
 * Current members still owe the latest version when that version chases every open membership.
 * An acceptance of an older version, a deleted acceptance, or a new-members-only document does not count.
 * Requires `memberships` and `accepted_documents` to be loaded.
 */
export function playerHasDocumentOutstanding(player: ClubPlayer, requiredDocuments: ClubDocument[]): boolean {
  if (!player.isCurrentMember) return false;

  const latest = ClubDocument.onlyLastVersion(requiredDocuments.filter((doc) => doc.name && !doc.is_deleted));
  return latest.some((doc) => documentChasesCurrentMembers(doc) && !player.hasAcceptedDocumentVersion(doc));
}

export function memberBookNeedsAttention(
  player: ClubPlayer,
  club: Club,
  lastPlayed: ClubPlayerLastPlayed | undefined,
  requiredDocuments: ClubDocument[],
): boolean {
  return getMemberBookAttentionReasons(player, club, lastPlayed, requiredDocuments).length > 0;
}

export function attentionReasonLabel(reason: MemberBookAttentionReason): string {
  switch (reason) {
    case "missing_email":
      return "Missing email";
    case "missing_phone":
      return "Missing phone";
    case "inactive":
      return "Inactive";
    case "document_outstanding":
      return "Document outstanding";
    case "not_played_recently":
      return "Not played recently";
  }
}

export function complianceStatusLabel(
  player: ClubPlayer,
  club: Club,
  lastPlayed: ClubPlayerLastPlayed | undefined,
  requiredDocuments: ClubDocument[],
): string {
  return memberBookNeedsAttention(player, club, lastPlayed, requiredDocuments) ? "Needs attention" : "OK";
}
